/**
 * WhatsApp Assignment Canvas — Project ↔ Team Member ↔ WhatsApp Number
 *
 * Assumptions (third-party WhatsApp phase):
 * - Meta WABA is paused; numbers are org-managed phone lines for a future third-party sender.
 * - Project → many members (JobAssignment).
 * - Member → many numbers; each number has at most one owner.
 * - Canvas manages relationships only; sending is a later POST to the third party.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Unlink, UserMinus } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import api from '../../lib/api';
import './whatsappAssignmentCanvas.css';

type WaNumber = {
  id: string;
  phoneE164: string;
  label: string | null;
  notes: string | null;
  userId: string | null;
  userName: string | null;
};

type CanvasMember = {
  id: string;
  name: string;
  avatarUrl?: string | null;
  roleInOrg: string;
  numberIds: string[];
};

type ProjectMember = {
  userId: string;
  name: string;
  role: string;
};

type CanvasProject = {
  id: string;
  title: string;
  status: string;
  brand: string;
  members: ProjectMember[];
};

type CanvasData = {
  organization: { id: string; name: string; type: string };
  numbers: WaNumber[];
  members: CanvasMember[];
  projects: CanvasProject[];
};

type NodeType = 'project' | 'member' | 'number';
type Selection = { type: NodeType; id: string } | null;
type FadeMode = 'dim' | 'grey' | 'blur';
type ViewMode = 'lanes' | 'radial';
type PopoverState = { type: NodeType; id: string } | null;
type DragPayload = { type: NodeType; id: string };
type ReassignConfirm = {
  numberId: string;
  userId: string;
  phoneLabel: string;
  fromName: string;
  toName: string;
};

type LayoutBox = { x: number; y: number; w: number; h: number };

const NODE_W = 168;
const NODE_H = 78;
const LANE = { project: 28, member: 300, number: 572 };
const TOP = 48;
const GAP = 18;
const DRAG_MIME = 'application/x-pkc-node';
const RADIAL_CX = 520;
const RADIAL_CY = 420;
const RADIAL_INNER_R = 230;
const RADIAL_OUTER_R = 400;

function keyOf(type: string, id: string) {
  return `${type}:${id}`;
}

function phoneLabel(n: WaNumber | null | undefined) {
  if (!n) return '—';
  return n.phoneE164 || n.label || n.id;
}

function relatedSets(data: CanvasData, sel: Selection) {
  const projects = new Set<string>();
  const members = new Set<string>();
  const numbers = new Set<string>();
  if (!sel) return { projects, members, numbers };

  if (sel.type === 'project') {
    const p = data.projects.find((x) => x.id === sel.id);
    if (!p) return { projects, members, numbers };
    projects.add(p.id);
    p.members.forEach((m) => {
      members.add(m.userId);
      const mem = data.members.find((x) => x.id === m.userId);
      mem?.numberIds.forEach((nid) => numbers.add(nid));
    });
    return { projects, members, numbers };
  }

  if (sel.type === 'member') {
    const m = data.members.find((x) => x.id === sel.id);
    if (!m) return { projects, members, numbers };
    members.add(m.id);
    m.numberIds.forEach((nid) => numbers.add(nid));
    data.projects.forEach((p) => {
      if (p.members.some((pm) => pm.userId === m.id)) projects.add(p.id);
    });
    return { projects, members, numbers };
  }

  numbers.add(sel.id);
  const n = data.numbers.find((x) => x.id === sel.id);
  if (n?.userId) {
    members.add(n.userId);
    data.projects.forEach((p) => {
      if (p.members.some((pm) => pm.userId === n.userId)) projects.add(p.id);
    });
  }
  return { projects, members, numbers };
}

function bezierPath(a: LayoutBox, b: LayoutBox) {
  const x1 = a.x + a.w;
  const y1 = a.y + a.h / 2;
  const x2 = b.x;
  const y2 = b.y + b.h / 2;
  const cx = (x1 + x2) / 2;
  return `M ${x1} ${y1} C ${cx} ${y1}, ${cx} ${y2}, ${x2} ${y2}`;
}

/** Straight center-to-center link for radial focus view. */
function radialPath(a: LayoutBox, b: LayoutBox) {
  const x1 = a.x + a.w / 2;
  const y1 = a.y + a.h / 2;
  const x2 = b.x + b.w / 2;
  const y2 = b.y + b.h / 2;
  return `M ${x1} ${y1} L ${x2} ${y2}`;
}

function placeOnRing(
  map: Map<string, LayoutBox>,
  keys: string[],
  cx: number,
  cy: number,
  radius: number,
  startAngle = -Math.PI / 2
) {
  const n = Math.max(keys.length, 1);
  keys.forEach((k, i) => {
    const theta = startAngle + (i / n) * Math.PI * 2;
    map.set(k, {
      x: cx + radius * Math.cos(theta) - NODE_W / 2,
      y: cy + radius * Math.sin(theta) - NODE_H / 2,
      w: NODE_W,
      h: NODE_H,
    });
  });
}

function computeLanesLayout(data: CanvasData): {
  map: Map<string, LayoutBox>;
  size: { w: number; h: number };
} {
  const map = new Map<string, LayoutBox>();
  let y = TOP;
  data.projects.forEach((p) => {
    map.set(keyOf('project', p.id), { x: LANE.project, y, w: NODE_W, h: NODE_H });
    y += NODE_H + GAP;
  });
  y = TOP;
  data.members.forEach((m) => {
    map.set(keyOf('member', m.id), { x: LANE.member, y, w: NODE_W, h: NODE_H });
    y += NODE_H + GAP;
  });
  y = TOP;
  data.numbers.forEach((n) => {
    map.set(keyOf('number', n.id), { x: LANE.number, y, w: NODE_W, h: NODE_H });
    y += NODE_H + GAP;
  });
  const maxY = Math.max(
    TOP + data.projects.length * (NODE_H + GAP),
    TOP + data.members.length * (NODE_H + GAP),
    TOP + data.numbers.length * (NODE_H + GAP),
    720
  );
  return { map, size: { w: LANE.number + NODE_W + 48, h: maxY + 40 } };
}

/**
 * Radial focus layout (product-canvas style):
 * - With selection: hub = selected, inner ring = related, outer = rest
 * - Idle: members / numbers / projects on three concentric rings
 */
function computeRadialLayout(
  data: CanvasData,
  selected: Selection,
  related: { projects: Set<string>; members: Set<string>; numbers: Set<string> }
): { map: Map<string, LayoutBox>; size: { w: number; h: number } } {
  const map = new Map<string, LayoutBox>();
  const cx = RADIAL_CX;
  const cy = RADIAL_CY;
  const pad = RADIAL_OUTER_R + NODE_W;
  const size = { w: cx + pad, h: cy + pad };

  if (!selected) {
    placeOnRing(
      map,
      data.members.map((m) => keyOf('member', m.id)),
      cx,
      cy,
      170
    );
    placeOnRing(
      map,
      data.numbers.map((n) => keyOf('number', n.id)),
      cx,
      cy,
      290
    );
    placeOnRing(
      map,
      data.projects.map((p) => keyOf('project', p.id)),
      cx,
      cy,
      410
    );
    return { map, size: { w: cx + 410 + NODE_W, h: cy + 410 + NODE_H } };
  }

  const hubKey = keyOf(selected.type, selected.id);
  map.set(hubKey, {
    x: cx - NODE_W / 2,
    y: cy - NODE_H / 2,
    w: NODE_W,
    h: NODE_H,
  });

  const inner: string[] = [];
  const outer: string[] = [];

  data.projects.forEach((p) => {
    const k = keyOf('project', p.id);
    if (k === hubKey) return;
    (related.projects.has(p.id) ? inner : outer).push(k);
  });
  data.members.forEach((m) => {
    const k = keyOf('member', m.id);
    if (k === hubKey) return;
    (related.members.has(m.id) ? inner : outer).push(k);
  });
  data.numbers.forEach((n) => {
    const k = keyOf('number', n.id);
    if (k === hubKey) return;
    (related.numbers.has(n.id) ? inner : outer).push(k);
  });

  // Keep related ring readable; spill overflow to outer.
  const innerCap = 18;
  const innerShown = inner.slice(0, innerCap);
  const innerSpill = inner.slice(innerCap);
  placeOnRing(map, innerShown, cx, cy, RADIAL_INNER_R);
  placeOnRing(map, [...innerSpill, ...outer], cx, cy, RADIAL_OUTER_R);

  return { map, size };
}

function parseDragPayload(e: React.DragEvent): DragPayload | null {
  try {
    const raw = e.dataTransfer.getData(DRAG_MIME) || e.dataTransfer.getData('text/plain');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DragPayload;
    if (
      parsed &&
      (parsed.type === 'project' || parsed.type === 'member' || parsed.type === 'number') &&
      typeof parsed.id === 'string'
    ) {
      return parsed;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function canLink(from: NodeType, to: NodeType) {
  if (from === to) return false;
  const pair = new Set([from, to]);
  return (
    (pair.has('member') && pair.has('number')) || (pair.has('project') && pair.has('member'))
  );
}

export default function WhatsAppAssignmentCanvas() {
  const { user } = useAuthStore();
  const orgId = user?.organizationId || '';
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Selection>(null);
  const [view, setView] = useState<ViewMode>('lanes');
  const [fade, setFade] = useState<FadeMode>('dim');
  const [hideUnlinked, setHideUnlinked] = useState(false);
  const [hidePicker, setHidePicker] = useState(false);
  const [hideInsight, setHideInsight] = useState(false);
  const [isNarrow, setIsNarrow] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');
  const [toast, setToast] = useState<string | null>(null);
  const [popover, setPopover] = useState<PopoverState>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const [draggingKey, setDraggingKey] = useState<string | null>(null);
  const [reassignConfirm, setReassignConfirm] = useState<ReassignConfirm | null>(null);
  const [creatingNumber, setCreatingNumber] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const stageScrollRef = useRef<HTMLDivElement>(null);
  const panRef = useRef<{ x: number; y: number; sl: number; st: number } | null>(null);
  const mobileDefaultsApplied = useRef(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 980px)');
    const sync = () => {
      const narrow = mq.matches;
      setIsNarrow(narrow);
      if (narrow && !mobileDefaultsApplied.current) {
        mobileDefaultsApplied.current = true;
        setHidePicker(true);
        setHideInsight(true);
      }
    };
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2400);
  }, []);

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['agency', 'whatsapp-canvas', orgId] });
  }, [orgId, queryClient]);

  const canvasQuery = useQuery({
    queryKey: ['agency', 'whatsapp-canvas', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await api.get<CanvasData>(`/organizations/${orgId}/whatsapp-canvas`);
      return data;
    },
  });

  const data = canvasQuery.data;

  const errMsg = (err: unknown, fallback: string) => {
    if (err && typeof err === 'object' && 'response' in err) {
      return (err as { response?: { data?: { error?: string } } }).response?.data?.error || fallback;
    }
    return fallback;
  };

  const assignNumber = useMutation({
    mutationFn: async ({ numberId, userId }: { numberId: string; userId: string | null }) => {
      const { data: res } = await api.put(`/organizations/${orgId}/whatsapp-numbers/${numberId}/member`, {
        userId,
      });
      return res;
    },
    onSuccess: (_res, vars) => {
      invalidate();
      setPopover(null);
      setReassignConfirm(null);
      const n = data?.numbers.find((x) => x.id === vars.numberId);
      const m = data?.members.find((x) => x.id === vars.userId);
      showToast(
        vars.userId
          ? `Linked · ${m?.name || 'Member'} → ${n ? phoneLabel(n) : 'number'}`
          : `Cleared owner on ${n ? phoneLabel(n) : 'number'}`
      );
    },
    onError: (err) => showToast(errMsg(err, 'Failed to link number')),
  });

  const addProjectMember = useMutation({
    mutationFn: async ({ jobId, userId }: { jobId: string; userId: string }) => {
      const { data: res } = await api.post(`/organizations/${orgId}/jobs/${jobId}/canvas-members`, {
        userId,
        role: 'COLLABORATOR',
      });
      return res;
    },
    onSuccess: (_res, vars) => {
      invalidate();
      setPopover(null);
      const p = data?.projects.find((x) => x.id === vars.jobId);
      const m = data?.members.find((x) => x.id === vars.userId);
      showToast(`Added · ${m?.name || 'Member'} → ${p?.title || 'project'}`);
    },
    onError: (err) => showToast(errMsg(err, 'Failed to add project member')),
  });

  const removeProjectMember = useMutation({
    mutationFn: async ({ jobId, userId }: { jobId: string; userId: string }) => {
      await api.delete(`/organizations/${orgId}/jobs/${jobId}/canvas-members/${userId}`);
    },
    onSuccess: (_res, vars) => {
      invalidate();
      setPopover(null);
      const p = data?.projects.find((x) => x.id === vars.jobId);
      const m = data?.members.find((x) => x.id === vars.userId);
      showToast(`Removed · ${m?.name || 'Member'} from ${p?.title || 'project'}`);
    },
    onError: (err) => showToast(errMsg(err, 'Failed to remove project member')),
  });

  const createNumber = useMutation({
    mutationFn: async ({ phoneE164, label }: { phoneE164: string; label: string }) => {
      const { data: res } = await api.post(`/organizations/${orgId}/whatsapp-numbers`, {
        phoneE164,
        label: label || null,
      });
      return res as WaNumber;
    },
    onSuccess: (created) => {
      // Keep cache in sync immediately so Insight doesn't crash on a stale selection.
      queryClient.setQueryData<CanvasData>(['agency', 'whatsapp-canvas', orgId], (prev) => {
        if (!prev || !created?.id) return prev;
        if (prev.numbers.some((n) => n.id === created.id)) return prev;
        return {
          ...prev,
          numbers: [
            ...prev.numbers,
            {
              id: created.id,
              phoneE164: created.phoneE164,
              label: created.label ?? null,
              notes: created.notes ?? null,
              userId: created.userId ?? null,
              userName: created.userName ?? null,
            },
          ],
        };
      });
      invalidate();
      setCreatingNumber(false);
      setNewPhone('');
      setNewLabel('');
      showToast(`Created · ${created.phoneE164}`);
      if (created?.id) setSelected({ type: 'number', id: created.id });
    },
    onError: (err) => showToast(errMsg(err, 'Failed to create number')),
  });

  const deleteNumber = useMutation({
    mutationFn: async (numberId: string) => {
      await api.delete(`/organizations/${orgId}/whatsapp-numbers/${numberId}`);
    },
    onSuccess: () => {
      invalidate();
      setPopover(null);
      setSelected(null);
      showToast('Number deleted');
    },
    onError: (err) => showToast(errMsg(err, 'Failed to delete number')),
  });

  const busy =
    assignNumber.isPending ||
    addProjectMember.isPending ||
    removeProjectMember.isPending ||
    createNumber.isPending ||
    deleteNumber.isPending;

  const linkMemberNumber = useCallback(
    (userId: string, numberId: string) => {
      if (!data) return;
      const member = data.members.find((m) => m.id === userId);
      const number = data.numbers.find((n) => n.id === numberId);
      if (!member || !number) return;

      if (number.userId === userId) {
        showToast('Already linked');
        setPopover(null);
        return;
      }

      if (number.userId && number.userId !== userId) {
        const from = data.members.find((m) => m.id === number.userId);
        setReassignConfirm({
          numberId,
          userId,
          phoneLabel: phoneLabel(number),
          fromName: from?.name || number.userName || 'Member',
          toName: member.name || 'Member',
        });
        setPopover(null);
        return;
      }

      assignNumber.mutate({ numberId, userId });
    },
    [assignNumber, data, showToast]
  );

  const linkProjectMember = useCallback(
    (jobId: string, userId: string) => {
      if (!data) return;
      const project = data.projects.find((p) => p.id === jobId);
      if (!project) return;
      if (project.members.some((m) => m.userId === userId)) {
        showToast('Already on this project');
        setPopover(null);
        return;
      }
      addProjectMember.mutate({ jobId, userId });
    },
    [addProjectMember, data, showToast]
  );

  const applyDrop = useCallback(
    (from: DragPayload, toType: NodeType, toId: string) => {
      if (!canLink(from.type, toType)) return;
      if (
        (from.type === 'member' && toType === 'number') ||
        (from.type === 'number' && toType === 'member')
      ) {
        const userId = from.type === 'member' ? from.id : toId;
        const numberId = from.type === 'number' ? from.id : toId;
        linkMemberNumber(userId, numberId);
        return;
      }
      if (
        (from.type === 'project' && toType === 'member') ||
        (from.type === 'member' && toType === 'project')
      ) {
        const jobId = from.type === 'project' ? from.id : toId;
        const userId = from.type === 'member' ? from.id : toId;
        linkProjectMember(jobId, userId);
      }
    },
    [linkMemberNumber, linkProjectMember]
  );

  const related = useMemo(
    () =>
      data
        ? relatedSets(data, selected)
        : { projects: new Set<string>(), members: new Set<string>(), numbers: new Set<string>() },
    [data, selected]
  );

  const { layout, stageSize } = useMemo(() => {
    if (!data) {
      return {
        layout: new Map<string, LayoutBox>(),
        stageSize: { w: 860, h: 720 },
      };
    }
    if (view === 'radial') {
      const result = computeRadialLayout(data, selected, related);
      return { layout: result.map, stageSize: result.size };
    }
    const result = computeLanesLayout(data);
    return { layout: result.map, stageSize: result.size };
  }, [data, view, selected, related]);

  const scrollToNode = useCallback(
    (type: string, id: string) => {
      const pos = layout.get(keyOf(type, id));
      const el = stageScrollRef.current;
      if (!pos || !el) return;
      if (view === 'radial') {
        el.scrollTo({
          left: Math.max(0, pos.x + NODE_W / 2 - el.clientWidth / 2),
          top: Math.max(0, pos.y + NODE_H / 2 - el.clientHeight / 2),
          behavior: 'smooth',
        });
        return;
      }
      el.scrollTo({ left: Math.max(0, pos.x - 40), top: Math.max(0, pos.y - 40), behavior: 'smooth' });
    },
    [layout, view]
  );

  // Center the hub when entering radial focus or changing selection in radial.
  useEffect(() => {
    if (view !== 'radial' || !selected) return;
    const t = window.setTimeout(() => scrollToNode(selected.type, selected.id), 50);
    return () => window.clearTimeout(t);
  }, [view, selected, scrollToNode]);

  const selectNode = useCallback(
    (type: NodeType, id: string, scroll = false) => {
      setSelected({ type, id });
      if (scroll) scrollToNode(type, id);
      if (window.matchMedia('(max-width: 980px)').matches) {
        setHideInsight(false);
        setHidePicker(true);
      }
    },
    [scrollToNode]
  );

  const openPlus = useCallback(
    (type: NodeType, id: string, e?: React.SyntheticEvent) => {
      e?.stopPropagation();
      e?.preventDefault();
      selectNode(type, id);
      setPopover((prev) => (prev?.type === type && prev.id === id ? null : { type, id }));
    },
    [selectNode]
  );

  useEffect(() => {
    if (!popover) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest('.pkc-popover') || t.closest('.pkc-plus')) return;
      setPopover(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPopover(null);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [popover]);

  // Drop selection if the entity disappeared after a refetch/delete.
  useEffect(() => {
    if (!data || !selected) return;
    const exists =
      selected.type === 'project'
        ? data.projects.some((p) => p.id === selected.id)
        : selected.type === 'member'
          ? data.members.some((m) => m.id === selected.id)
          : data.numbers.some((n) => n.id === selected.id);
    if (!exists) {
      setSelected(null);
      setPopover(null);
    }
  }, [data, selected]);

  useEffect(() => {
    const el = stageScrollRef.current;
    if (!el) return;
    const onDown = (e: PointerEvent) => {
      if ((e.target as HTMLElement).closest('.pkc-node')) return;
      if ((e.target as HTMLElement).closest('.pkc-popover')) return;
      panRef.current = { x: e.clientX, y: e.clientY, sl: el.scrollLeft, st: el.scrollTop };
      el.classList.add('is-panning');
      el.setPointerCapture(e.pointerId);
    };
    const onMove = (e: PointerEvent) => {
      if (!panRef.current) return;
      el.scrollLeft = panRef.current.sl - (e.clientX - panRef.current.x);
      el.scrollTop = panRef.current.st - (e.clientY - panRef.current.y);
    };
    const onUp = () => {
      panRef.current = null;
      el.classList.remove('is-panning');
    };
    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
    };
  }, [data]);

  if (!orgId) {
    return <div className="p-6 text-glass-text-2">No agency organization on your session.</div>;
  }
  if (canvasQuery.isLoading) {
    return <div className="p-6 text-glass-text-2">Loading WhatsApp canvas…</div>;
  }
  if (canvasQuery.isError || !data) {
    return (
      <div className="p-6 text-red-400">
        Failed to load canvas.{' '}
        <button type="button" className="underline" onClick={() => canvasQuery.refetch()}>
          Retry
        </button>
      </div>
    );
  }

  const filteredProjects = data.projects.filter((p) => {
    const q = projectSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      p.title.toLowerCase().includes(q) ||
      p.brand.toLowerCase().includes(q) ||
      p.status.toLowerCase().includes(q)
    );
  });

  const nodeClass = (type: NodeType, id: string) => {
    const k = keyOf(type, id);
    const classes = ['pkc-node'];
    if (selected?.type === type && selected.id === id) classes.push('pkc-is-primary');
    else if (
      (type === 'project' && related.projects.has(id)) ||
      (type === 'member' && related.members.has(id)) ||
      (type === 'number' && related.numbers.has(id))
    ) {
      classes.push('pkc-is-related');
    } else if (selected) {
      classes.push('pkc-is-faded');
    }

    let linked = false;
    if (type === 'project') {
      linked = (data.projects.find((p) => p.id === id)?.members.length || 0) > 0;
    } else if (type === 'member') {
      const m = data.members.find((x) => x.id === id);
      linked = !!(
        (m?.numberIds.length || 0) > 0 ||
        data.projects.some((p) => p.members.some((pm) => pm.userId === id))
      );
    } else {
      linked = !!data.numbers.find((n) => n.id === id)?.userId;
    }
    if (!linked) classes.push('pkc-is-unlinked');
    if (draggingKey === k) classes.push('pkc-is-dragging');
    if (dragOverKey === k) classes.push('pkc-is-drop-target');
    return classes.join(' ');
  };

  const edgeClass = (fromKey: string, toKey: string) => {
    if (!selected) return '';
    const [ft, fid] = fromKey.split(':');
    const [tt, tid] = toKey.split(':');
    const fromOk =
      (ft === 'project' && related.projects.has(fid)) ||
      (ft === 'member' && related.members.has(fid)) ||
      (ft === 'number' && related.numbers.has(fid));
    const toOk =
      (tt === 'project' && related.projects.has(tid)) ||
      (tt === 'member' && related.members.has(tid)) ||
      (tt === 'number' && related.numbers.has(tid));
    return fromOk && toOk ? 'pkc-is-related' : 'pkc-is-faded';
  };

  const links: string[] = [];
  const pathFn = view === 'radial' ? radialPath : bezierPath;
  data.projects.forEach((p) => {
    p.members.forEach((pm) => {
      const a = layout.get(keyOf('project', p.id));
      const b = layout.get(keyOf('member', pm.userId));
      if (!a || !b) return;
      links.push(
        `<path class="pkc-link ${edgeClass(keyOf('project', p.id), keyOf('member', pm.userId))}" d="${pathFn(a, b)}" />`
      );
    });
  });
  data.numbers.forEach((n) => {
    if (!n.userId) return;
    const a = layout.get(keyOf('member', n.userId));
    const b = layout.get(keyOf('number', n.id));
    if (!a || !b) return;
    links.push(
      `<path class="pkc-link ${edgeClass(keyOf('member', n.userId), keyOf('number', n.id))}" d="${pathFn(a, b)}" />`
    );
  });

  const onNodeDragStart = (type: NodeType, id: string, e: React.DragEvent) => {
    const payload: DragPayload = { type, id };
    e.dataTransfer.setData(DRAG_MIME, JSON.stringify(payload));
    e.dataTransfer.setData('text/plain', JSON.stringify(payload));
    e.dataTransfer.effectAllowed = 'link';
    setDraggingKey(keyOf(type, id));
    setPopover(null);
  };

  const onNodeDragEnd = () => {
    setDraggingKey(null);
    setDragOverKey(null);
  };

  const onNodeDragOver = (type: NodeType, id: string, e: React.DragEvent) => {
    if (!draggingKey) return;
    const [fromType] = draggingKey.split(':') as [NodeType, string];
    if (!canLink(fromType, type)) return;
    if (draggingKey === keyOf(type, id)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'link';
    setDragOverKey(keyOf(type, id));
  };

  const onNodeDragLeave = (type: NodeType, id: string) => {
    if (dragOverKey === keyOf(type, id)) setDragOverKey(null);
  };

  const onNodeDrop = (type: NodeType, id: string, e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverKey(null);
    setDraggingKey(null);
    const from = parseDragPayload(e);
    if (!from) return;
    applyDrop(from, type, id);
  };

  const popoverPos = popover ? layout.get(keyOf(popover.type, popover.id)) : null;

  return (
    <div
      className="pkc-root"
      style={{ height: '100%' }}
      data-fade={fade}
      data-view={view}
      data-hide-unlinked={hideUnlinked ? '1' : '0'}
      data-hide-picker={hidePicker ? '1' : '0'}
      data-hide-insight={hideInsight ? '1' : '0'}
      data-narrow={isNarrow ? '1' : '0'}
    >
      <div className="pkc-banner pkc-banner-desktop">
        <strong>Project ↔ Members ↔ WhatsApp numbers.</strong> Multi-member projects, multi-number
        members. Use <strong>+</strong> or drag to link. Switch to <strong>Radial</strong> to focus
        one node. Numbers are org-managed (third-party sender later — Meta WABA paused).
      </div>

      <div className="pkc-toolbar">
        <h1>WhatsApp assignment canvas</h1>
        <div className="pkc-seg pkc-seg-desktop" aria-label="View mode">
          {(['lanes', 'radial'] as ViewMode[]).map((mode) => (
            <button
              key={mode}
              type="button"
              className={view === mode ? 'is-on' : ''}
              onClick={() => setView(mode)}
            >
              {mode[0].toUpperCase() + mode.slice(1)}
            </button>
          ))}
        </div>
        <div className="pkc-seg pkc-seg-desktop" aria-label="Fade mode">
          {(['dim', 'grey', 'blur'] as FadeMode[]).map((mode) => (
            <button
              key={mode}
              type="button"
              className={fade === mode ? 'is-on' : ''}
              onClick={() => setFade(mode)}
            >
              {mode[0].toUpperCase() + mode.slice(1)}
            </button>
          ))}
        </div>
        <button type="button" className="pkc-btn pkc-btn-desktop" onClick={() => setHideUnlinked((v) => !v)}>
          {hideUnlinked ? 'Show unlinked' : 'Hide unlinked'}
        </button>
        <button
          type="button"
          className={`pkc-btn ${!hidePicker ? 'pkc-btn-primary' : ''}`}
          onClick={() => {
            setHidePicker((v) => !v);
            if (isNarrow) setHideInsight(true);
          }}
        >
          {hidePicker ? 'Projects' : 'Hide projects'}
        </button>
        <button
          type="button"
          className={`pkc-btn ${!hideInsight ? 'pkc-btn-primary' : ''}`}
          onClick={() => {
            setHideInsight((v) => !v);
            if (isNarrow) setHidePicker(true);
          }}
        >
          {hideInsight ? 'Insight' : 'Hide insight'}
        </button>
        <button
          type="button"
          className="pkc-btn pkc-btn-ghost pkc-btn-desktop"
          onClick={() => {
            setSelected(null);
            setPopover(null);
          }}
        >
          Clear selection
        </button>
        <div className="pkc-spacer" />
        <button type="button" className="pkc-btn" onClick={() => setCreatingNumber(true)}>
          <Plus size={14} /> Add number
        </button>
        <button type="button" className="pkc-btn pkc-btn-desktop" onClick={() => canvasQuery.refetch()}>
          Refresh
        </button>
      </div>

      {view === 'radial' && !selected ? (
        <div className="pkc-radial-hint">
          Select a project, member, or number to put it in the center (related on the inner ring).
        </div>
      ) : null}

      <div className="pkc-jump">
        <label>
          Projects
          <select
            defaultValue=""
            onChange={(e) => {
              if (!e.target.value) return;
              selectNode('project', e.target.value, true);
              e.target.value = '';
            }}
          >
            <option value="">Jump…</option>
            {data.projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.title}
              </option>
            ))}
          </select>
        </label>
        <label>
          Members
          <select
            defaultValue=""
            onChange={(e) => {
              if (!e.target.value) return;
              selectNode('member', e.target.value, true);
              e.target.value = '';
            }}
          >
            <option value="">Jump…</option>
            {data.members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Numbers
          <select
            defaultValue=""
            onChange={(e) => {
              if (!e.target.value) return;
              selectNode('number', e.target.value, true);
              e.target.value = '';
            }}
          >
            <option value="">Jump…</option>
            {data.numbers.map((n) => (
              <option key={n.id} value={n.id}>
                {phoneLabel(n)}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="pkc-body">
        {!hidePicker && isNarrow ? (
          <button
            type="button"
            className="pkc-panel-scrim"
            aria-label="Close projects panel"
            onClick={() => setHidePicker(true)}
          />
        ) : null}
        <aside className="pkc-picker" aria-hidden={hidePicker}>
          <div className="pkc-panel-head">
            <p className="pkc-side-title">Projects</p>
            <button
              type="button"
              className="pkc-btn pkc-btn-ghost pkc-panel-close"
              onClick={() => setHidePicker(true)}
            >
              Close
            </button>
          </div>
          <input
            className="pkc-search"
            type="search"
            placeholder="Search projects…"
            value={projectSearch}
            onChange={(e) => setProjectSearch(e.target.value)}
          />
          <div className="pkc-pick-list">
            {filteredProjects.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`pkc-pick-item ${selected?.type === 'project' && selected.id === p.id ? 'is-active' : ''}`}
                onClick={() => selectNode('project', p.id, true)}
              >
                <span className="t">{p.title}</span>
                <span className="s">
                  {p.brand || '—'} · {p.members.length} member{p.members.length === 1 ? '' : 's'}
                </span>
              </button>
            ))}
            {!filteredProjects.length ? <p className="pkc-insight-empty">No projects match.</p> : null}
          </div>
        </aside>

        <div className="pkc-stage-wrap">
          <div className="pkc-stage-scroll" ref={stageScrollRef}>
            <div className="pkc-stage" style={{ width: stageSize.w, height: stageSize.h }}>
              <svg
                className="pkc-links"
                width={stageSize.w}
                height={stageSize.h}
                dangerouslySetInnerHTML={{ __html: links.join('') }}
              />
              {view === 'lanes' ? (
                <>
                  <div className="pkc-lane-label" style={{ left: LANE.project }}>
                    Projects
                  </div>
                  <div className="pkc-lane-label" style={{ left: LANE.member }}>
                    Team members
                  </div>
                  <div className="pkc-lane-label" style={{ left: LANE.number }}>
                    WhatsApp numbers
                  </div>
                </>
              ) : selected ? (
                <>
                  <div
                    className="pkc-radial-ring"
                    style={{
                      left: RADIAL_CX - RADIAL_INNER_R,
                      top: RADIAL_CY - RADIAL_INNER_R,
                      width: RADIAL_INNER_R * 2,
                      height: RADIAL_INNER_R * 2,
                    }}
                  />
                  <div
                    className="pkc-radial-ring pkc-radial-ring-outer"
                    style={{
                      left: RADIAL_CX - RADIAL_OUTER_R,
                      top: RADIAL_CY - RADIAL_OUTER_R,
                      width: RADIAL_OUTER_R * 2,
                      height: RADIAL_OUTER_R * 2,
                    }}
                  />
                </>
              ) : null}

              {data.projects.map((p) => {
                const pos = layout.get(keyOf('project', p.id))!;
                return (
                  <div
                    key={p.id}
                    role="button"
                    tabIndex={0}
                    draggable
                    data-type="project"
                    className={nodeClass('project', p.id)}
                    style={{ left: pos.x, top: pos.y }}
                    onClick={() => selectNode('project', p.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') selectNode('project', p.id);
                      if (e.key === '+' || e.key === '=') openPlus('project', p.id, e);
                    }}
                    onDragStart={(e) => onNodeDragStart('project', p.id, e)}
                    onDragEnd={onNodeDragEnd}
                    onDragOver={(e) => onNodeDragOver('project', p.id, e)}
                    onDragLeave={() => onNodeDragLeave('project', p.id)}
                    onDrop={(e) => onNodeDrop('project', p.id, e)}
                  >
                    <button
                      type="button"
                      className="pkc-plus"
                      title="Add member"
                      aria-label="Add member"
                      draggable={false}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => openPlus('project', p.id, e)}
                    >
                      <Plus size={14} strokeWidth={2.5} />
                    </button>
                    <div className="pkc-node-title">{p.title}</div>
                    <div className="pkc-node-sub">
                      {p.brand || '—'} · {p.status}
                    </div>
                    <div className="pkc-pills">
                      {p.members.length ? (
                        <span className="pkc-pill pkc-pill-primary">
                          {p.members.length} member{p.members.length === 1 ? '' : 's'}
                        </span>
                      ) : (
                        <span className="pkc-pill pkc-pill-warn">No members</span>
                      )}
                    </div>
                  </div>
                );
              })}

              {data.members.map((m) => {
                const pos = layout.get(keyOf('member', m.id))!;
                const jobs = data.projects.filter((p) => p.members.some((pm) => pm.userId === m.id)).length;
                return (
                  <div
                    key={m.id}
                    role="button"
                    tabIndex={0}
                    draggable
                    data-type="member"
                    className={nodeClass('member', m.id)}
                    style={{ left: pos.x, top: pos.y }}
                    onClick={() => selectNode('member', m.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') selectNode('member', m.id);
                      if (e.key === '+' || e.key === '=') openPlus('member', m.id, e);
                    }}
                    onDragStart={(e) => onNodeDragStart('member', m.id, e)}
                    onDragEnd={onNodeDragEnd}
                    onDragOver={(e) => onNodeDragOver('member', m.id, e)}
                    onDragLeave={() => onNodeDragLeave('member', m.id)}
                    onDrop={(e) => onNodeDrop('member', m.id, e)}
                  >
                    <button
                      type="button"
                      className="pkc-plus"
                      title="Link number or project"
                      aria-label="Link number or project"
                      draggable={false}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => openPlus('member', m.id, e)}
                    >
                      <Plus size={14} strokeWidth={2.5} />
                    </button>
                    <div className="pkc-node-title">{m.name}</div>
                    <div className="pkc-node-sub">
                      {m.roleInOrg} · {jobs} project{jobs === 1 ? '' : 's'}
                    </div>
                    <div className="pkc-pills">
                      <span className="pkc-pill pkc-pill-role">{m.roleInOrg}</span>
                      {m.numberIds.length ? (
                        <span className="pkc-pill pkc-pill-wa">
                          {m.numberIds.length} number{m.numberIds.length === 1 ? '' : 's'}
                        </span>
                      ) : (
                        <span className="pkc-pill pkc-pill-warn">No number</span>
                      )}
                    </div>
                  </div>
                );
              })}

              {data.numbers.map((n) => {
                const pos = layout.get(keyOf('number', n.id))!;
                return (
                  <div
                    key={n.id}
                    role="button"
                    tabIndex={0}
                    draggable
                    data-type="number"
                    className={nodeClass('number', n.id)}
                    style={{ left: pos.x, top: pos.y }}
                    onClick={() => selectNode('number', n.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') selectNode('number', n.id);
                      if (e.key === '+' || e.key === '=') openPlus('number', n.id, e);
                    }}
                    onDragStart={(e) => onNodeDragStart('number', n.id, e)}
                    onDragEnd={onNodeDragEnd}
                    onDragOver={(e) => onNodeDragOver('number', n.id, e)}
                    onDragLeave={() => onNodeDragLeave('number', n.id)}
                    onDrop={(e) => onNodeDrop('number', n.id, e)}
                  >
                    <button
                      type="button"
                      className="pkc-plus pkc-plus-left"
                      title="Assign owner"
                      aria-label="Assign owner"
                      draggable={false}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => openPlus('number', n.id, e)}
                    >
                      <Plus size={14} strokeWidth={2.5} />
                    </button>
                    <div className="pkc-node-title">{phoneLabel(n)}</div>
                    <div className="pkc-node-sub">{n.label || 'Org WhatsApp line'}</div>
                    <div className="pkc-pills">
                      {n.userId ? (
                        <span className="pkc-pill pkc-pill-wa">
                          {(n.userName || 'Member').split(' ')[0]}
                        </span>
                      ) : (
                        <span className="pkc-pill pkc-pill-warn">Unassigned</span>
                      )}
                    </div>
                  </div>
                );
              })}

              {!data.numbers.length ? (
                <div
                  style={{
                    position: 'absolute',
                    left: LANE.number,
                    top: TOP,
                    width: NODE_W + 40,
                    fontSize: 13,
                    color: '#64748b',
                  }}
                >
                  No numbers yet. Use “Add number” in the toolbar.
                </div>
              ) : null}

              {popover && popoverPos ? (
                <AssignPopover
                  data={data}
                  popover={popover}
                  left={
                    popover.type === 'number'
                      ? popoverPos.x - 228
                      : popoverPos.x + popoverPos.w + 10
                  }
                  top={popoverPos.y}
                  busy={busy}
                  onClose={() => setPopover(null)}
                  onAddMemberToProject={(userId) => linkProjectMember(popover.id, userId)}
                  onRemoveMemberFromProject={(userId) =>
                    removeProjectMember.mutate({ jobId: popover.id, userId })
                  }
                  onAddNumberToMember={(numberId) => linkMemberNumber(popover.id, numberId)}
                  onClearNumberFromMember={(numberId) =>
                    assignNumber.mutate({ numberId, userId: null })
                  }
                  onAddMemberToProjectFromMember={(jobId) => linkProjectMember(jobId, popover.id)}
                  onRemoveMemberFromProjectFromMember={(jobId) =>
                    removeProjectMember.mutate({ jobId, userId: popover.id })
                  }
                  onAssignOwner={(userId) => linkMemberNumber(userId, popover.id)}
                  onClearOwner={() => assignNumber.mutate({ numberId: popover.id, userId: null })}
                  onDeleteNumber={() => {
                    if (window.confirm('Delete this WhatsApp number?')) {
                      deleteNumber.mutate(popover.id);
                    }
                  }}
                />
              ) : null}
            </div>
          </div>
        </div>

        <aside className="pkc-insight" aria-hidden={hideInsight}>
          <div className="pkc-panel-head">
            <p className="pkc-side-title">Insight</p>
            <button
              type="button"
              className="pkc-btn pkc-btn-ghost pkc-panel-close"
              onClick={() => setHideInsight(true)}
            >
              Close
            </button>
          </div>
          {!selected ? (
            <div className="pkc-insight-empty">
              Select a node, use <strong>+</strong>, or drag between lanes. Members can hold many
              numbers; projects can have many members.
            </div>
          ) : selected.type === 'project' ? (
            <ProjectInsight
              data={data}
              projectId={selected.id}
              onJumpMember={(id) => selectNode('member', id, true)}
              onRemove={(userId) => removeProjectMember.mutate({ jobId: selected.id, userId })}
              busy={busy}
            />
          ) : selected.type === 'member' ? (
            <MemberInsight
              data={data}
              memberId={selected.id}
              onJumpProject={(id) => selectNode('project', id, true)}
              onJumpNumber={(id) => selectNode('number', id, true)}
              onUnlinkNumber={(numberId) => assignNumber.mutate({ numberId, userId: null })}
              busy={busy}
            />
          ) : (
            <NumberInsight
              data={data}
              numberId={selected.id}
              onJumpMember={(id) => selectNode('member', id, true)}
              onClear={() => assignNumber.mutate({ numberId: selected.id, userId: null })}
              onDelete={() => {
                if (window.confirm('Delete this WhatsApp number?')) {
                  deleteNumber.mutate(selected.id);
                }
              }}
              busy={busy}
            />
          )}
        </aside>
        {!hideInsight && isNarrow ? (
          <button
            type="button"
            className="pkc-panel-scrim pkc-panel-scrim-insight"
            aria-label="Close insight panel"
            onClick={() => setHideInsight(true)}
          />
        ) : null}
      </div>

      {creatingNumber ? (
        <div className="pkc-modal-backdrop" role="presentation" onClick={() => setCreatingNumber(false)}>
          <div className="pkc-modal" role="dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Add WhatsApp number</h3>
            <p>Stored for the agency — used later when posting to your third-party WhatsApp sender.</p>
            <div className="pkc-field">
              <label htmlFor="new-phone">Phone (E.164 preferred)</label>
              <input
                id="new-phone"
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                placeholder="+60123456789"
              />
            </div>
            <div className="pkc-field">
              <label htmlFor="new-label">Label (optional)</label>
              <input
                id="new-label"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="Ops / Beauty / Main"
              />
            </div>
            <div className="pkc-actions">
              <button type="button" className="pkc-btn" onClick={() => setCreatingNumber(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="pkc-btn pkc-btn-primary"
                disabled={busy || !newPhone.trim()}
                onClick={() =>
                  createNumber.mutate({ phoneE164: newPhone.trim(), label: newLabel.trim() })
                }
              >
                {busy ? 'Saving…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {reassignConfirm ? (
        <div
          className="pkc-modal-backdrop"
          role="presentation"
          onClick={() => setReassignConfirm(null)}
        >
          <div className="pkc-modal" role="dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Move number to another member?</h3>
            <p>
              <strong>{reassignConfirm.phoneLabel}</strong> is owned by{' '}
              <strong>{reassignConfirm.fromName}</strong>. Assign it to{' '}
              <strong>{reassignConfirm.toName}</strong>? (A number has one owner; members may still
              own many numbers.)
            </p>
            <div className="pkc-actions">
              <button type="button" className="pkc-btn" onClick={() => setReassignConfirm(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="pkc-btn pkc-btn-primary"
                disabled={busy}
                onClick={() =>
                  assignNumber.mutate({
                    numberId: reassignConfirm.numberId,
                    userId: reassignConfirm.userId,
                  })
                }
              >
                {busy ? 'Saving…' : 'Move number'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {toast ? <div className="pkc-toast show">{toast}</div> : null}
    </div>
  );
}

function AssignPopover({
  data,
  popover,
  left,
  top,
  busy,
  onClose,
  onAddMemberToProject,
  onRemoveMemberFromProject,
  onAddNumberToMember,
  onClearNumberFromMember,
  onAddMemberToProjectFromMember,
  onRemoveMemberFromProjectFromMember,
  onAssignOwner,
  onClearOwner,
  onDeleteNumber,
}: {
  data: CanvasData;
  popover: { type: NodeType; id: string };
  left: number;
  top: number;
  busy: boolean;
  onClose: () => void;
  onAddMemberToProject: (userId: string) => void;
  onRemoveMemberFromProject: (userId: string) => void;
  onAddNumberToMember: (numberId: string) => void;
  onClearNumberFromMember: (numberId: string) => void;
  onAddMemberToProjectFromMember: (jobId: string) => void;
  onRemoveMemberFromProjectFromMember: (jobId: string) => void;
  onAssignOwner: (userId: string) => void;
  onClearOwner: () => void;
  onDeleteNumber: () => void;
}) {
  const [tab, setTab] = useState<'number' | 'project'>('number');
  useEffect(() => setTab('number'), [popover.type, popover.id]);

  let title = 'Assign';
  if (popover.type === 'project') title = 'Add / remove members';
  if (popover.type === 'member') title = 'Quick link';
  if (popover.type === 'number') title = 'Assign owner';

  return (
    <div
      className="pkc-popover"
      style={{ left, top }}
      role="dialog"
      aria-label={title}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="pkc-popover-head">
        <span>{title}</span>
        <button type="button" className="pkc-popover-close" onClick={onClose} aria-label="Close">
          Close
        </button>
      </div>

      {popover.type === 'project' ? (
        <>
          <p className="pkc-popover-hint">Projects support many members.</p>
          <div className="pkc-popover-list">
            {data.members.map((m) => {
              const onProject = data.projects
                .find((p) => p.id === popover.id)
                ?.members.some((pm) => pm.userId === m.id);
              return (
                <button
                  key={m.id}
                  type="button"
                  className={`pkc-popover-item ${onProject ? 'is-current' : ''}`}
                  disabled={busy}
                  onClick={() =>
                    onProject ? onRemoveMemberFromProject(m.id) : onAddMemberToProject(m.id)
                  }
                >
                  <span className="t">{m.name}</span>
                  <span className="s">
                    {m.roleInOrg}
                    {onProject ? ' · on project (click to remove)' : ' · click to add'}
                  </span>
                </button>
              );
            })}
          </div>
        </>
      ) : null}

      {popover.type === 'member' ? (
        <>
          <div className="pkc-popover-tabs">
            <button
              type="button"
              className={tab === 'number' ? 'is-on' : ''}
              onClick={() => setTab('number')}
            >
              Numbers
            </button>
            <button
              type="button"
              className={tab === 'project' ? 'is-on' : ''}
              onClick={() => setTab('project')}
            >
              Projects
            </button>
          </div>
          {tab === 'number' ? (
            <>
              <p className="pkc-popover-hint">This member can own many numbers.</p>
              <div className="pkc-popover-list">
                {!data.numbers.length ? (
                  <p className="pkc-popover-hint">No numbers yet — use Add number.</p>
                ) : (
                  data.numbers.map((n) => {
                    const isMine = n.userId === popover.id;
                    const otherOwner =
                      n.userId && n.userId !== popover.id
                        ? n.userName || data.members.find((m) => m.id === n.userId)?.name
                        : null;
                    return (
                      <button
                        key={n.id}
                        type="button"
                        className={`pkc-popover-item ${isMine ? 'is-current' : ''}`}
                        disabled={busy}
                        onClick={() =>
                          isMine ? onClearNumberFromMember(n.id) : onAddNumberToMember(n.id)
                        }
                      >
                        <span className="t">{phoneLabel(n)}</span>
                        <span className="s">
                          {n.label || '—'}
                          {otherOwner ? ` · now ${otherOwner}` : ''}
                          {isMine ? ' · linked (click to clear)' : ' · click to link'}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </>
          ) : (
            <>
              <p className="pkc-popover-hint">Add or remove this member from projects.</p>
              <div className="pkc-popover-list">
                {data.projects.map((p) => {
                  const onProject = p.members.some((pm) => pm.userId === popover.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      className={`pkc-popover-item ${onProject ? 'is-current' : ''}`}
                      disabled={busy}
                      onClick={() =>
                        onProject
                          ? onRemoveMemberFromProjectFromMember(p.id)
                          : onAddMemberToProjectFromMember(p.id)
                      }
                    >
                      <span className="t">{p.title}</span>
                      <span className="s">
                        {p.brand || '—'}
                        {onProject ? ' · on project (click to remove)' : ' · click to add'}
                      </span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </>
      ) : null}

      {popover.type === 'number' ? (
        <>
          <p className="pkc-popover-hint">One owner per number; that owner may have many lines.</p>
          <div className="pkc-popover-list">
            {data.members.map((m) => {
              const isCurrent = data.numbers.find((n) => n.id === popover.id)?.userId === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  className={`pkc-popover-item ${isCurrent ? 'is-current' : ''}`}
                  disabled={busy || isCurrent}
                  onClick={() => onAssignOwner(m.id)}
                >
                  <span className="t">{m.name}</span>
                  <span className="s">
                    {m.roleInOrg}
                    {isCurrent ? ' · current' : ''}
                  </span>
                </button>
              );
            })}
          </div>
          {data.numbers.find((n) => n.id === popover.id)?.userId ? (
            <button
              type="button"
              className="pkc-btn pkc-btn-ghost pkc-popover-clear"
              disabled={busy}
              onClick={onClearOwner}
            >
              Clear owner
            </button>
          ) : null}
          <button
            type="button"
            className="pkc-btn pkc-btn-ghost pkc-popover-clear"
            disabled={busy}
            onClick={onDeleteNumber}
          >
            Delete number
          </button>
        </>
      ) : null}
    </div>
  );
}

function ProjectInsight({
  data,
  projectId,
  onJumpMember,
  onRemove,
  busy,
}: {
  data: CanvasData;
  projectId: string;
  onJumpMember: (id: string) => void;
  onRemove: (userId: string) => void;
  busy: boolean;
}) {
  const p = data.projects.find((x) => x.id === projectId);
  if (!p) {
    return <div className="pkc-insight-empty">Project no longer available. Clear selection or refresh.</div>;
  }
  return (
    <div className="pkc-insight-card">
      <h3>{p.title}</h3>
      <p className="sub">
        {p.brand || '—'} · {p.status}
      </p>
      <div className="pkc-stat">
        <span>Members</span>
        <strong>{p.members.length}</strong>
      </div>
      {p.members.map((m) => (
        <div key={m.userId} className="pkc-actions" style={{ marginTop: 6 }}>
          <button type="button" className="pkc-pick-item" style={{ flex: 1 }} onClick={() => onJumpMember(m.userId)}>
            <span className="t">{m.name}</span>
            <span className="s">{m.role}</span>
          </button>
          <button
            type="button"
            className="pkc-btn"
            disabled={busy}
            onClick={() => onRemove(m.userId)}
            title="Remove from project"
            aria-label="Remove from project"
          >
            <UserMinus size={14} />
          </button>
        </div>
      ))}
      {!p.members.length ? <p className="pkc-hint">No members — use + on the node.</p> : null}
    </div>
  );
}

function MemberInsight({
  data,
  memberId,
  onJumpProject,
  onJumpNumber,
  onUnlinkNumber,
  busy,
}: {
  data: CanvasData;
  memberId: string;
  onJumpProject: (id: string) => void;
  onJumpNumber: (id: string) => void;
  onUnlinkNumber: (numberId: string) => void;
  busy: boolean;
}) {
  const m = data.members.find((x) => x.id === memberId);
  if (!m) {
    return <div className="pkc-insight-empty">Member no longer available. Clear selection or refresh.</div>;
  }
  const projects = data.projects.filter((p) => p.members.some((pm) => pm.userId === m.id));
  const numbers = data.numbers.filter((n) => n.userId === m.id);
  return (
    <>
      <div className="pkc-insight-card">
        <h3>{m.name}</h3>
        <p className="sub">{m.roleInOrg}</p>
        <div className="pkc-stat">
          <span>Projects</span>
          <strong>{projects.length}</strong>
        </div>
        <div className="pkc-stat">
          <span>WhatsApp numbers</span>
          <strong>{numbers.length}</strong>
        </div>
      </div>
      <div className="pkc-insight-card">
        <h3>Numbers</h3>
        {numbers.map((n) => (
          <div key={n.id} className="pkc-actions" style={{ marginTop: 6 }}>
            <button type="button" className="pkc-pick-item" style={{ flex: 1 }} onClick={() => onJumpNumber(n.id)}>
              <span className="t">{phoneLabel(n)}</span>
              <span className="s">{n.label || '—'}</span>
            </button>
            <button
              type="button"
              className="pkc-btn"
              disabled={busy}
              onClick={() => onUnlinkNumber(n.id)}
              title="Unlink number"
              aria-label="Unlink number"
            >
              <Unlink size={14} />
            </button>
          </div>
        ))}
        {!numbers.length ? <p className="pkc-hint">No numbers — use + or drag a number here.</p> : null}
      </div>
      <div className="pkc-insight-card">
        <h3>Projects</h3>
        {projects.map((p) => (
          <button
            key={p.id}
            type="button"
            className="pkc-pick-item"
            style={{ width: '100%', marginBottom: 6 }}
            onClick={() => onJumpProject(p.id)}
          >
            <span className="t">{p.title}</span>
            <span className="s">{p.brand}</span>
          </button>
        ))}
        {!projects.length ? <p className="pkc-hint">Not on any project yet.</p> : null}
      </div>
    </>
  );
}

function NumberInsight({
  data,
  numberId,
  onJumpMember,
  onClear,
  onDelete,
  busy,
}: {
  data: CanvasData;
  numberId: string;
  onJumpMember: (id: string) => void;
  onClear: () => void;
  onDelete: () => void;
  busy: boolean;
}) {
  const n = data.numbers.find((x) => x.id === numberId);
  if (!n) {
    return <div className="pkc-insight-empty">Number no longer available. Clear selection or refresh.</div>;
  }
  return (
    <div className="pkc-insight-card">
      <h3>{phoneLabel(n)}</h3>
      <p className="sub">{n.label || 'Org WhatsApp line'}</p>
      <div className="pkc-stat">
        <span>Owner</span>
        <strong>
          {n.userId ? (
            <button type="button" className="underline" onClick={() => onJumpMember(n.userId!)}>
              {n.userName || 'Member'}
            </button>
          ) : (
            'Unassigned'
          )}
        </strong>
      </div>
      <div className="pkc-actions" style={{ marginTop: 12 }}>
        {n.userId ? (
          <button type="button" className="pkc-btn" disabled={busy} onClick={onClear}>
            Clear owner
          </button>
        ) : null}
        <button type="button" className="pkc-btn" disabled={busy} onClick={onDelete}>
          Delete
        </button>
      </div>
    </div>
  );
}
